import axios from "axios";

let instance = null;
const CSRF_COOKIE_NAME = "X-CSRF-Token";
const CSRF_HEADER_NAME = "X-CSRF-Token";

class Request {
  constructor() {
    if (instance) {
      return instance;
    }

    this._csrfToken = "";
    this._csrfTokenPromise = null;
    this._csrfTokenReady = false;

    this._ensureCSRFToken().catch((err) => {
      console.log(err);
    });

    instance = this;
  }

  _ensureCSRFToken() {
    if (this._csrfTokenReady) {
      return Promise.resolve();
    }

    if (this._csrfTokenPromise) {
      return this._csrfTokenPromise;
    }

    const config = {
        url: "/csrf",
        method: "get",
        headers: { [CSRF_HEADER_NAME]: "Fetch" },
        withCredentials: true,
        xsrfCookieName: CSRF_COOKIE_NAME,
        xsrfHeaderName: CSRF_HEADER_NAME,
      };

    this._csrfTokenPromise = axios(config)
      .then((response) => {
        const headerToken = response?.headers?.["x-csrf-token"] || response?.headers?.[CSRF_HEADER_NAME];
        const cookieMatch =
          typeof document !== "undefined"
            ? document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]+)`))
            : null;
        const cookieToken = cookieMatch ? decodeURIComponent(cookieMatch[1]) : "";
        const nextToken = headerToken || cookieToken;

        if (!nextToken) {
          throw new Error("Unable to initialize CSRF token.");
        }

        this._csrfToken = nextToken;
        this._csrfTokenReady = true;
        return response;
      })
      .catch((error) => {
        this._csrfTokenPromise = null;
        this._csrfTokenReady = false;
        throw error;
      });

    return this._csrfTokenPromise;
  }

  _injectCSRFToken(config) {
    const nextConfig = {
      ...config,
      headers: {
        ...(config.headers || {}),
      },
    };

    if (!this._csrfToken) {
      return nextConfig;
    }

    nextConfig.headers[CSRF_HEADER_NAME] = this._csrfToken;

    if (
      nextConfig.data &&
      typeof nextConfig.data === "object" &&
      !Array.isArray(nextConfig.data) &&
      !(typeof FormData !== "undefined" && nextConfig.data instanceof FormData)
    ) {
      nextConfig.data = {
        ...nextConfig.data,
        _csrf: this._csrfToken,
      };
    }

    return nextConfig;
  }

  _isCSRFError(error) {
    return error?.response?.status === 403 && error?.response?.data?.error === "EBADCSRFTOKEN";
  }

  async _axiosExecute(config) {
    let nextConfig = {
      ...config,
      withCredentials: true,
      xsrfCookieName: CSRF_COOKIE_NAME,
      xsrfHeaderName: CSRF_HEADER_NAME,
    };
    const method = String(nextConfig.method || "get").toLowerCase();

    if (method !== "get") {
      await this._ensureCSRFToken();
      nextConfig = this._injectCSRFToken(nextConfig);
    }

    try {
      return await axios(nextConfig);
    } catch (error) {
      if (method === "get" || !this._isCSRFError(error)) {
        console.log(error);
        throw error;
      }

      this._csrfToken = "";
      this._csrfTokenReady = false;
      this._csrfTokenPromise = null;

      await this._ensureCSRFToken();
      return axios(this._injectCSRFToken(nextConfig));
    }
  }

  get(url, payload) {
    return new Promise((resolve, reject) => {
      const config = {
        url: url,
        method: "get",
        headers: {},
        data: payload,
      };

      this._axiosExecute(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  post(url, payload) {
    return new Promise((resolve, reject) => {
      const config = {
        url: url,
        method: "post",
        headers: {},
        data: payload || {},
      };

      this._axiosExecute(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  put(url, payload) {
    return new Promise((resolve, reject) => {
      const config = {
        url: url,
        method: "put",
        headers: {},
        data: payload,
      };

      this._axiosExecute(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  delete(url, payload) {
    return new Promise((resolve, reject) => {
      const config = {
        url: url,
        method: "delete",
        headers: {},
        data: payload,
      };

      this._axiosExecute(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}

export default Request;
